import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Section } from "@/components/app/Primitives";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const _key = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `r_${Math.random().toString(36).slice(2, 8)}`;

const blankInfluencer = () => ({
  _key: _key(),
  influencer_name: "",
  influencer_email: "",
  influencer_mobile: "",
  influencer_country: "India",
  services: [{ _k: _key(), type: "Instagram Reel", count: 1, description: "", fee: 0 }],
  influencer_payment_terms_days: 15,
  customer_terms_days: 60,
  advance_required: false,
  advance_percentage: 50,
  advance_trigger: "on_po_acceptance",
  expected_delivery_date: "",
  notes: "",
});

const SAMPLE = {
  tenant_id: "unilever",
  campaign_name: "Diwali 2026 · Bru Coffee Festive Push",
  product: "Bru Gold Coffee",
  adfluence_ref: "ADF-EMAIL-778899",
  briefing_note: "Diwali festive push, primary metro cities focus. Two Instagram Reels + one YouTube Short. Delivery by Oct 25, 2026.",
  influencers: [
    { ...blankInfluencer(), influencer_name: "Priya Sharma", influencer_email: "priya.influencer@example.com", influencer_mobile: "+91-9876543210",
      services: [{ _k: _key(), type: "Instagram Reel", count: 2, description: "Diwali morning ritual", fee: 55000 }],
      advance_required: true, advance_percentage: 50 },
    { ...blankInfluencer(), influencer_name: "Arjun Kapoor", influencer_email: "arjun.influencer@example.com", influencer_mobile: "+91-9876543211",
      services: [{ _k: _key(), type: "YouTube Short", count: 3, description: "Festive coffee routine", fee: 72000 }] },
  ],
};

export default function AdFluenceIntake() {
  const nav = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    tenant_id: "unilever",
    campaign_name: "",
    product: "",
    adfluence_ref: "",
    briefing_note: "",
    influencers: [blankInfluencer()],
  });

  useEffect(() => {
    api.get("/tenants").then((r) => setTenants((r.data || []).filter((t) => !t.is_propay && !t.is_network && !t.is_audit_partner))).catch(() => {});
  }, []);

  const patch = (k, v) => setF({ ...f, [k]: v });
  const patchInf = (i, k, v) => setF({ ...f, influencers: f.influencers.map((inf, idx) => idx === i ? { ...inf, [k]: v } : inf) });
  const patchSvc = (i, sIdx, k, v) => setF({
    ...f,
    influencers: f.influencers.map((inf, idx) => idx !== i ? inf : {
      ...inf,
      services: inf.services.map((s, ii) => ii === sIdx ? { ...s, [k]: v } : s),
    }),
  });
  const addSvc = (i) => setF({
    ...f,
    influencers: f.influencers.map((inf, idx) => idx !== i ? inf : {
      ...inf, services: [...inf.services, { _k: _key(), type: "Instagram Post", count: 1, description: "", fee: 0 }],
    }),
  });
  const rmSvc = (i, sIdx) => setF({
    ...f,
    influencers: f.influencers.map((inf, idx) => idx !== i ? inf : {
      ...inf, services: inf.services.filter((_, ii) => ii !== sIdx),
    }),
  });
  const addInf = () => setF({ ...f, influencers: [...f.influencers, blankInfluencer()] });
  const rmInf = (i) => setF({ ...f, influencers: f.influencers.filter((_, idx) => idx !== i) });

  const fillSample = () => setF({ ...SAMPLE, influencers: SAMPLE.influencers.map((x) => ({ ...x })) });

  const submit = async () => {
    if (!f.campaign_name.trim()) return toast.error("Campaign name is required");
    if (!f.influencers.length) return toast.error("At least one influencer line is required");
    setBusy(true);
    try {
      const body = {
        tenant_id: f.tenant_id,
        campaign_name: f.campaign_name.trim(),
        product: f.product,
        adfluence_ref: f.adfluence_ref,
        briefing_note: f.briefing_note,
        influencers: f.influencers.map(({ _key: _k, ...rest }) => ({
          ...rest,
          services: rest.services.map(({ _k: _kk, ...s }) => ({ ...s, fee: Number(s.fee) || 0, count: Number(s.count) || 1 })),
          influencer_payment_terms_days: Number(rest.influencer_payment_terms_days) || 15,
          customer_terms_days: Number(rest.customer_terms_days) || 60,
          advance_percentage: Number(rest.advance_percentage) || 0,
        })),
      };
      const r = await api.post("/campaigns/adfluence-intake", body);
      toast.success(`Campaign ${r.data.campaign.campaign_number} created — ${r.data.orders.length} influencer orders queued.`);
      nav(`/procurement/campaigns/${r.data.campaign.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Intake failed");
    } finally {
      setBusy(false);
    }
  };

  const totalCogs = f.influencers.reduce((a, inf) => a + inf.services.reduce((b, s) => b + (Number(s.fee) || 0), 0), 0);

  return (
    <div className="space-y-6" data-testid="adfluence-intake">
      <Section
        title="New AdFluence Intake"
        subtitle="Paste the campaign details you received from AdFluence via email. Each influencer becomes a separate ProPay order under one campaign umbrella."
      />

      <div className="rounded-xl border-2 border-dashed border-[#0052FF]/30 bg-[#0052FF]/5 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-[#0052FF]">Demo helper</div>
            <div className="text-sm text-slate-700 mt-1">Load a sample Unilever × Bru Coffee campaign with 2 influencers.</div>
          </div>
          <Button type="button" variant="outline" onClick={fillSample} data-testid="fill-sample-adfluence-btn">⚡ Fill sample AdFluence email</Button>
        </div>
      </div>

      <div className="rounded-xl border bg-white p-4 space-y-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">① Campaign header</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">FMCG Customer *</label>
            <select
              className="w-full h-10 rounded-md border border-input px-3 text-sm bg-white"
              value={f.tenant_id}
              onChange={(e) => patch("tenant_id", e.target.value)}
              data-testid="intake-tenant"
            >
              {tenants.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">AdFluence reference (email subject / thread ID)</label>
            <Input value={f.adfluence_ref} onChange={(e) => patch("adfluence_ref", e.target.value)} placeholder="ADF-EMAIL-778899" data-testid="intake-adfluence-ref" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Campaign name *</label>
            <Input value={f.campaign_name} onChange={(e) => patch("campaign_name", e.target.value)} placeholder="Diwali 2026 · Bru Coffee Festive Push" data-testid="intake-campaign-name" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Product</label>
            <Input value={f.product} onChange={(e) => patch("product", e.target.value)} placeholder="Bru Gold Coffee" data-testid="intake-product" />
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-500">Briefing note (from AdFluence email body)</label>
          <Textarea value={f.briefing_note} onChange={(e) => patch("briefing_note", e.target.value)} placeholder="Paste briefing notes here…" rows={3} data-testid="intake-briefing" />
        </div>
      </div>

      <div className="rounded-xl border bg-white p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">② Influencers ({f.influencers.length})</div>
          <Button type="button" size="sm" variant="outline" onClick={addInf} data-testid="add-influencer-btn">+ Add influencer</Button>
        </div>

        {f.influencers.map((inf, i) => (
          <div key={inf._key} className="rounded-lg border p-3 space-y-3" data-testid={`influencer-row-${i}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold uppercase tracking-widest text-[#0052FF]">Influencer #{i + 1}</div>
              {f.influencers.length > 1 && <button type="button" onClick={() => rmInf(i)} className="text-rose-600 text-xs">Remove</button>}
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Input placeholder="Name *" value={inf.influencer_name} onChange={(e) => patchInf(i, "influencer_name", e.target.value)} data-testid={`inf-name-${i}`} />
              <Input placeholder="Email *" type="email" value={inf.influencer_email} onChange={(e) => patchInf(i, "influencer_email", e.target.value)} data-testid={`inf-email-${i}`} />
              <Input placeholder="Mobile *" value={inf.influencer_mobile} onChange={(e) => patchInf(i, "influencer_mobile", e.target.value)} data-testid={`inf-mobile-${i}`} />
            </div>
            <div>
              <div className="text-[11px] text-slate-500 mb-1">Deliverables — one line each</div>
              {inf.services.map((s, sIdx) => (
                <div key={s._k} className="grid grid-cols-12 gap-2 mb-1 items-center">
                  <Input className="col-span-3" placeholder="Type" value={s.type} onChange={(e) => patchSvc(i, sIdx, "type", e.target.value)} />
                  <Input className="col-span-1" type="number" placeholder="Qty" value={s.count} onChange={(e) => patchSvc(i, sIdx, "count", parseInt(e.target.value) || 1)} />
                  <Input className="col-span-5" placeholder="Description" value={s.description} onChange={(e) => patchSvc(i, sIdx, "description", e.target.value)} />
                  <Input className="col-span-2" type="number" placeholder="Fee (INR)" value={s.fee} onChange={(e) => patchSvc(i, sIdx, "fee", parseFloat(e.target.value) || 0)} />
                  <button type="button" onClick={() => rmSvc(i, sIdx)} className="col-span-1 text-rose-600 text-xs">✕</button>
                </div>
              ))}
              <button type="button" onClick={() => addSvc(i)} className="text-xs text-[#0052FF] font-semibold">+ Add deliverable</button>
            </div>
            <div className="grid sm:grid-cols-4 gap-2">
              <div>
                <label className="text-[11px] text-slate-500">Influencer terms (days)</label>
                <Input type="number" value={inf.influencer_payment_terms_days} onChange={(e) => patchInf(i, "influencer_payment_terms_days", parseInt(e.target.value) || 15)} />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Customer terms (days)</label>
                <Input type="number" value={inf.customer_terms_days} onChange={(e) => patchInf(i, "customer_terms_days", parseInt(e.target.value) || 60)} />
              </div>
              <div>
                <label className="text-[11px] text-slate-500">Expected delivery</label>
                <Input type="date" value={inf.expected_delivery_date || ""} onChange={(e) => patchInf(i, "expected_delivery_date", e.target.value)} />
              </div>
              <label className="flex items-end gap-2 text-xs pb-2">
                <input type="checkbox" checked={inf.advance_required} onChange={(e) => patchInf(i, "advance_required", e.target.checked)} data-testid={`inf-advance-${i}`} />
                Advance
                {inf.advance_required && (
                  <Input className="w-16" type="number" value={inf.advance_percentage} onChange={(e) => patchInf(i, "advance_percentage", parseFloat(e.target.value) || 0)} />
                )}
                {inf.advance_required && <span className="text-[10px] text-slate-500">%</span>}
              </label>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between border-t pt-3">
        <div className="text-sm text-slate-500">
          <b>Total influencer cost (COGS)</b>: <span className="font-mono font-semibold text-slate-800">INR {Number(totalCogs).toLocaleString()}</span> · {f.influencers.length} influencer{f.influencers.length > 1 ? "s" : ""}
        </div>
        <Button
          type="button"
          onClick={submit}
          disabled={busy || !f.campaign_name || totalCogs <= 0}
          className="bg-[#0052FF] hover:bg-[#0043D9] px-8 h-11"
          data-testid="submit-adfluence-intake-btn"
        >
          {busy ? "Creating campaign…" : "Create Campaign + Influencer Orders"}
        </Button>
      </div>
    </div>
  );
}
